import type { DisplayRule, Question } from '@/services/types/checklist.types';

/**
 * Avalia se uma regra de exibição é atendida.
 * Retorna true se a pergunta deve ser MOSTRADA, false se deve ser ESCONDIDA.
 */
export const evaluateDisplayRule = (
  rule: DisplayRule | undefined,
  answerValue: any
): boolean => {
  if (!rule) return true;

  const { condition, value: ruleValue } = rule;
  const answerStr = convertAnswerToString(answerValue);
  const ruleValueStr = ruleValue || '';

  switch (condition) {
    case 'equals':
      return answerStr === ruleValueStr;
    case 'not_equals':
    case 'notEquals':
      return answerStr !== ruleValueStr;
    case 'contains':
      return answerStr.toLowerCase().includes(ruleValueStr.toLowerCase());
    case 'not_contains':
    case 'notContains':
      return !answerStr.toLowerCase().includes(ruleValueStr.toLowerCase());
    case 'greater_than':
    case 'greaterThan':
      return compareNumeric(answerStr, ruleValueStr, (a, b) => a > b);
    case 'less_than':
    case 'lessThan':
      return compareNumeric(answerStr, ruleValueStr, (a, b) => a < b);
    case 'greaterOrEqual':
    case 'greater_or_equal':
      return compareNumeric(answerStr, ruleValueStr, (a, b) => a >= b);
    case 'lessOrEqual':
    case 'less_or_equal':
      return compareNumeric(answerStr, ruleValueStr, (a, b) => a <= b);
    case 'isEmpty':
      return isValueEmpty(answerValue);
    case 'isNotEmpty':
      return !isValueEmpty(answerValue);
    default:
      return true;
  }
};

const convertAnswerToString = (value: any): string => {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const isValueEmpty = (value: any): boolean => {
  if (value === null || value === undefined || value === '') return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
};

const compareNumeric = (
  answerStr: string,
  ruleValueStr: string,
  compareFn: (a: number, b: number) => boolean
): boolean => {
  const answerNum = parseFloat(answerStr);
  const ruleNum = parseFloat(ruleValueStr);
  if (isNaN(answerNum) || isNaN(ruleNum)) return false;
  return compareFn(answerNum, ruleNum);
};

export const findPreviousQuestion = (
  questions: Question[],
  currentQuestion: Question
): Question | null => {
  const sorted = [...questions].sort((a, b) => a.order - b.order);
  const currentIndex = sorted.findIndex(q => q.id === currentQuestion.id);
  if (currentIndex <= 0) return null;
  return sorted[currentIndex - 1];
};
